import React, { useState, useRef, useMemo } from 'react';
import { IconButton } from '../../components/ld/IconButton';
import { MIcon } from '../../lib/micon';
import { LdSheet } from '../../components/ui/LdSheet';
import heic2any from 'heic2any';
import { KlasseFachEintrag, SetupTab, ExtrahierterName } from '../../types';
import { useSchueler } from '../../contexts/SchuelerContext';
import { API_BASE, apiHeaders, apiPost, leseFallback, zeigeApiFehler } from '../../lib/api';
import { getAktuellerNutzer } from '../../lib/aktuellerNutzer';
import { toast } from '../../lib/toast';

export function KlasseEinrichtenModal({ defaultTab = 'upload', onClose }: {
  defaultTab?: SetupTab;
  onClose: () => void;
}) {
  const { addSchueler, schuelerListe } = useSchueler();
  const [tab, setTab] = useState<SetupTab>(defaultTab);

  // ── Foto-Tab ──────────────────────────────────────────────────────────────
  const fotoRef = useRef<HTMLInputElement>(null);
  const [fotoPreview, setFotoPreview] = useState('');
  const [fotoLaden,   setFotoLaden]   = useState(false);
  const [fotoFehler,  setFotoFehler]  = useState('');
  const [fotoNamen,   setFotoNamen]   = useState<ExtrahierterName[]>([]);
  const [fotoSel,     setFotoSel]     = useState<boolean[]>([]);

  async function onFotoPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFotoFehler(''); setFotoNamen([]); setFotoSel([]);
    setFotoPreview(''); // niemals HEIC direkt als <img> rendern

    // ── Schritt 1: HEIC per Magic-Bytes erkennen (zuverlässiger als file.type) ──
    let isHeic = false;
    try {
      const header = await file.slice(0, 12).arrayBuffer();
      const b = new Uint8Array(header);
      // HEIC/HEIF: Bytes 4–7 = 'ftyp' (0x66 0x74 0x79 0x70)
      const hasFtyp = b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70;
      if (hasFtyp) {
        const brand = String.fromCharCode(b[8], b[9], b[10], b[11]).toLowerCase();
        isHeic = ['heic', 'heix', 'hevc', 'hevx', 'mif1', 'msf1'].includes(brand);
      }
    } catch (magicErr) {
      console.warn('[FOTO] Magic-Byte Lesen fehlgeschlagen, fallback auf type/name:', magicErr);
    }
    // Fallback: Extension und MIME-Type (für den Fall dass arrayBuffer fehlschlägt)
    if (!isHeic) {
      isHeic = file.type === 'image/heic' ||
               file.type === 'image/heif' ||
               /\.(heic|heif)$/i.test(file.name);
    }

    // ── Schritt 2: HEIC → JPEG konvertieren ────────────────────────────────
    let processedFile: File = file;
    if (isHeic) {
      setFotoLaden(true);
      try {
        const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
        const blob: Blob = Array.isArray(converted) ? converted[0] : converted;
        // Sicherheitsprüfung: Blob muss Daten enthalten
        if (!blob || blob.size === 0) {
          throw new Error('heic2any lieferte leeren Blob');
        }
        processedFile = new File(
          [blob],
          file.name.replace(/\.(heic|heif)$/i, '.jpg'),
          { type: 'image/jpeg' }
        );
      } catch (heicErr) {
        console.error('[FOTO] heic2any Fehler:', heicErr);
        setFotoFehler('HEIC-Konvertierung fehlgeschlagen. Bitte das Foto in der Fotos-App als JPEG exportieren.');
        setFotoLaden(false);
        return;
      } finally {
        setFotoLaden(false);
      }
    }

    // ── Schritt 3: FileReader mit Validierung ──────────────────────────────
    setFotoLaden(true);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload  = ev => {
          const result = ev.target?.result as string;
          if (!result?.startsWith('data:')) {
            reject(new Error('FileReader lieferte kein gültiges Data-URL'));
          } else {
            resolve(result);
          }
        };
        reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden'));
        reader.readAsDataURL(processedFile);
      });

      // Prüfe ob Format von Claude API unterstützt wird
      const ok = dataUrl.startsWith('data:image/jpeg') ||
                 dataUrl.startsWith('data:image/png')  ||
                 dataUrl.startsWith('data:image/webp') ||
                 dataUrl.startsWith('data:image/gif');
      if (!ok) {
        const detected = dataUrl.substring(5, 30);
        console.error('[FOTO] Nicht unterstütztes Format:', detected);
        throw new Error(`Format nicht unterstützt: ${detected}. Bitte JPEG oder PNG verwenden.`);
      }

      setFotoPreview(dataUrl);
    } catch (readErr: any) {
      console.error('[FOTO] FileReader Fehler:', readErr);
      setFotoFehler(readErr.message ?? 'Foto konnte nicht geladen werden.');
    } finally {
      setFotoLaden(false);
    }
  }

  async function analysiereFoto() {
    if (!fotoPreview) return;
    setFotoLaden(true); setFotoFehler(''); setFotoNamen([]);
    try {
      const data = await apiPost<any>('/api/klassenliste-foto', { bild: fotoPreview });
      setFotoNamen(data);
      setFotoSel(data.map(() => true));
    } catch (err: any) {
      setFotoFehler(err.message ?? 'Analyse fehlgeschlagen.');
    } finally {
      setFotoLaden(false);
    }
  }

  async function uebernehmenFoto() {
    const kl = getAktuellerNutzer()?.klasse ?? '';
    const ausgewaehlte = fotoNamen.filter((_, i) => fotoSel[i]);
    const ok = await pruefeUndLegeFehlendeKlassenAn(ausgewaehlte);
    if (!ok) return;
    setImportFortschritt({ gesamt: ausgewaehlte.length, fertig: 0, duplikate: 0, fehler: 0 });
    let neu = 0, duplikat = 0, fehler = 0;
    for (const n of ausgewaehlte) {
      const extra = {
        email:          n.email          || undefined,
        eltern_email:   n.eltern_email   || undefined,
        eltern_telefon: n.eltern_telefon || undefined,
        geburtsdatum:   n.geburtsdatum   || undefined,
        geschlecht:     n.geschlecht     || undefined,
      };
      const ergebnis = await addSchueler(n.vorname, n.nachname, n.klasse ?? kl, extra);
      if (ergebnis === true) neu++;
      else if (ergebnis === false) duplikat++;
      else fehler++;
      setImportFortschritt({ gesamt: ausgewaehlte.length, fertig: neu + duplikat + fehler, duplikate: duplikat, fehler });
    }
    setImportFortschritt(null);
    onClose();
  }

  // ── Datei-Tab ─────────────────────────────────────────────────────────────
  const dateiRef = useRef<HTMLInputElement>(null);
  const [dateiName,   setDateiName]   = useState('');
  const [dateiLaden,  setDateiLaden]  = useState(false);
  const [dateiFehler, setDateiFehler] = useState('');
  const [dateiNamen,  setDateiNamen]  = useState<ExtrahierterName[]>([]);
  const [dateiSel,    setDateiSel]    = useState<boolean[]>([]);
  const [dateiFile,   setDateiFile]   = useState<File | null>(null);

  function onDateiPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setDateiFehler(''); setDateiNamen([]); setDateiSel([]);
    setDateiName(file.name);
    setDateiFile(file);
  }

  async function analysiereDatei() {
    if (!dateiFile) return;
    setDateiLaden(true); setDateiFehler(''); setDateiNamen([]);
    try {
      const form = new FormData();
      form.append('datei', dateiFile);
      const res  = await fetch(`${API_BASE}/api/klassenliste-datei`, { method: 'POST', body: form });
      const data = await res.json();
      if (!res.ok) throw new Error(data.fehler ?? 'Fehler');
      setDateiNamen(data);
      setDateiSel(data.map(() => true));
    } catch (err: any) {
      setDateiFehler(err.message ?? 'Analyse fehlgeschlagen.');
    } finally {
      setDateiLaden(false);
    }
  }

  async function uebernehmenDatei() {
    const kl = getAktuellerNutzer()?.klasse ?? '';
    const ausgewaehlte = dateiNamen.filter((_, i) => dateiSel[i]);
    const ok = await pruefeUndLegeFehlendeKlassenAn(ausgewaehlte);
    if (!ok) return;
    setImportFortschritt({ gesamt: ausgewaehlte.length, fertig: 0, duplikate: 0, fehler: 0 });
    let neu = 0, duplikat = 0, fehler = 0;
    for (const n of ausgewaehlte) {
      const extra = {
        email:          n.email          || undefined,
        eltern_email:   n.eltern_email   || undefined,
        eltern_telefon: n.eltern_telefon || undefined,
        geburtsdatum:   n.geburtsdatum   || undefined,
        geschlecht:     n.geschlecht     || undefined,
      };
      const ergebnis = await addSchueler(n.vorname, n.nachname, n.klasse ?? kl, extra);
      if (ergebnis === true) neu++;
      else if (ergebnis === false) duplikat++;
      else fehler++;
      setImportFortschritt({ gesamt: ausgewaehlte.length, fertig: neu + duplikat + fehler, duplikate: duplikat, fehler });
    }
    setImportFortschritt(null);
    onClose();
  }

  // ── Klassen-Prüfung vor Import ────────────────────────────────────────────

  async function pruefeUndLegeFehlendeKlassenAn(namen: ExtrahierterName[]): Promise<boolean> {
    const erkannt = Array.from(new Set(
      namen.map(n => n.klasse?.trim()).filter((k): k is string => !!k)
    ));
    if (erkannt.length === 0) return true;
    let vorhandene: string[] = [];
    try {
      const data: KlasseFachEintrag[] = await fetch(`${API_BASE}/api/meine-klassen`, {
        headers: apiHeaders(),
      }).then(leseFallback<KlasseFachEintrag[]>([], '/api/meine-klassen'));
      vorhandene = data.map(k => k.klasse_name.trim().toLowerCase());
    } catch { return true; }
    const unbekannt = erkannt.filter(n => !vorhandene.includes(n.toLowerCase()));
    if (unbekannt.length === 0) return true;
    return new Promise(resolve => {
      klassenConfirmResolveRef.current = resolve;
      setKlassenPruefung({
        klassen: unbekannt.map(name => ({ name, rolle: 'klassenleiter' as const })),
      });
    });
  }

  async function klassenBestaetigen() {
    if (!klassenPruefung) return;
    const m = new Date().getMonth() + 1;
    const y = new Date().getFullYear();
    const start = m >= 8 ? y : y - 1;
    const sj = `${start}/${start + 1}`;
    for (const k of klassenPruefung.klassen) {
      if (k.rolle === 'skip') continue;
      try {
        await apiPost('/api/klasse-fach', { klasse_name: k.name, schuljahr: sj, fach: 'Allgemein', rolle: k.rolle });
      } catch (e) { zeigeApiFehler(e, `Klasse "${k.name}" anlegen fehlgeschlagen`); }
    }
    window.dispatchEvent(new CustomEvent('lehrerdesk-klassen-reload'));
    setKlassenPruefung(null);
    klassenConfirmResolveRef.current?.(true);
    klassenConfirmResolveRef.current = null;
  }

  // ── Manuell-Tab ───────────────────────────────────────────────────────────
  const [vorname,         setVorname]         = useState('');
  const [nachname,        setNachname]        = useState('');
  const [klasse,          setKlasse]          = useState(getAktuellerNutzer()?.klasse ?? '');
  const [mEmail,          setMEmail]          = useState('');
  const [mElternEmail,    setMElternEmail]    = useState('');
  const [mElternTel,      setMElternTel]      = useState('');
  const [mGeburtsdatum,   setMGeburtsdatum]   = useState('');
  const [hinzgefuegt,     setHinzgefuegt]     = useState(0);
  // Klasse-Schnellwahl-Chips aus bereits vorhandenen Klassen (setzt denselben setKlasse-State; kein neuer State)
  const klassenChips = useMemo(
    () => Array.from(new Set(schuelerListe.map(s => s.klasse).filter((k): k is string => !!k && k !== '–'))).sort(),
    [schuelerListe],
  );

  async function handleSpeichern() {
    if (!vorname.trim() || !nachname.trim()) return;
    const extra = {
      email:          mEmail.trim()       || undefined,
      eltern_email:   mElternEmail.trim() || undefined,
      eltern_telefon: mElternTel.trim()   || undefined,
      geburtsdatum:   mGeburtsdatum.trim()|| undefined,
    };
    const neu = await addSchueler(vorname.trim(), nachname.trim(), klasse || '–', extra);
    if (neu === null) { toast('Fehler beim Speichern. Bitte Klasse prüfen und erneut versuchen.', 'error'); return; }
    if (!neu) { toast(`${vorname.trim()} ${nachname.trim()} ist bereits vorhanden.`, 'error'); return; }
    setVorname(''); setNachname(''); setMEmail(''); setMElternEmail(''); setMElternTel(''); setMGeburtsdatum('');
    setHinzgefuegt(h => h + 1);
  }

  // ── Styles ─────────────────────────────────────────────────────────────────
  const inputSt: React.CSSProperties = {
    width: '100%', padding: '11px 14px', borderRadius: 'var(--ld-radius-md)',
    border: '1px solid var(--ld-field-border)', fontSize: 15,
    boxSizing: 'border-box', color: 'var(--ld-text-primary)', background: 'var(--ld-bg-card)',
  };
  const labelSt: React.CSSProperties = {
    fontSize: 11, fontWeight: 700, color: 'var(--ld-text-secondary)', letterSpacing: .4, marginBottom: 6,
    display: 'flex', alignItems: 'center', gap: 5,
  };

  // Spinner component
  const Spinner = () => (
    <span style={{ width: 16, height: 16, border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin .6s linear infinite' }} />
  );

  // Namenliste mit Checkboxen + Inline-Bearbeitung + Fortschritt
  const [importFortschritt, setImportFortschritt] = useState<{ gesamt: number; fertig: number; duplikate: number; fehler: number } | null>(null);
  const klassenConfirmResolveRef = useRef<((ok: boolean) => void) | null>(null);
  const [klassenPruefung, setKlassenPruefung] = useState<{
    klassen: { name: string; rolle: 'klassenleiter' | 'fachlehrer' | 'skip' }[];
  } | null>(null);

  function NamenListe({ namen, sel, onToggle, onAlleUebernehmen, duplikatSet }: {
    namen: ExtrahierterName[]; sel: boolean[]; duplikatSet?: Set<number>;
    onToggle: (i: number) => void; onAlleUebernehmen: () => void;
  }) {
    const [bearbeiteIdx, setBearbeiteIdx] = useState<number | null>(null);
    const [editElternEmail, setEditElternEmail] = useState('');
    const anzahl = sel.filter(Boolean).length;
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ld-text-primary)' }}>
            {namen.length} Namen erkannt
          </div>
          <button onClick={() => namen.forEach((_, i) => sel[i] || onToggle(i))} style={{
            fontSize: 12, color: 'var(--ld-primary)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600,
          }}>Alle auswählen</button>
        </div>
        <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #F0F0F5', borderRadius: 'var(--ld-radius-md)', marginBottom: 14 }}>
          {namen.map((n, i) => (
            <div key={i} style={{
              borderBottom: i < namen.length - 1 ? '1px solid #F0F0F5' : 'none',
              background: duplikatSet?.has(i) ? '#FFF8EC' : sel[i] ? '#F0F9FF' : 'white',
            }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', cursor: 'pointer' }}>
                <input type="checkbox" checked={sel[i]} onChange={() => onToggle(i)}
                  style={{ width: 16, height: 16, accentColor: '#6B5FD4' }} />
                <span style={{ fontSize: 14, color: 'var(--ld-text-primary)', flex: 1 }}>{n.vorname} {n.nachname}</span>
                {duplikatSet?.has(i) && <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--ld-warning-text)', background: 'var(--ld-warning-soft)', padding: '2px 6px', borderRadius: 4 }}>Duplikat</span>}
                {!n.eltern_email && (
                  <button onClick={e => { e.preventDefault(); setBearbeiteIdx(i === bearbeiteIdx ? null : i); setEditElternEmail(n.eltern_email ?? ''); }} style={{
                    fontSize: 10, color: 'var(--ld-warning-text)', background: 'var(--ld-warning-soft)', border: 'none', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', fontWeight: 700,
                  }}>+ Elternmail</button>
                )}
                {n.klasse && <span style={{ fontSize: 11, color: 'var(--ld-text-secondary)' }}>{n.klasse}</span>}
              </label>
              {bearbeiteIdx === i && (
                <div style={{ padding: '4px 14px 10px 38px', display: 'flex', gap: 6 }}>
                  <input
                    placeholder="eltern@example.de"
                    value={editElternEmail}
                    onChange={e => setEditElternEmail(e.target.value)}
                    style={{ flex: 1, padding: '6px 10px', borderRadius: 'var(--ld-radius-sm)', border: '1.5px solid var(--ld-outline-soft)', fontSize: 13, outline: 'none' }}
                  />
                  <button onClick={() => { namen[i].eltern_email = editElternEmail; setBearbeiteIdx(null); }} style={{ padding: '6px 12px', background: 'var(--ld-primary)', color: 'white', border: 'none', borderRadius: 'var(--ld-radius-sm)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>OK</button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Fortschrittsanzeige */}
        {importFortschritt && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--ld-text-secondary)', marginBottom: 4 }}>
              <span>{importFortschritt.fertig} / {importFortschritt.gesamt} importiert</span>
              <span style={{ display: 'flex', gap: 8 }}>
                {importFortschritt.fehler > 0 && <span style={{ color: '#FF6B6B' }}>{importFortschritt.fehler} Fehler</span>}
                {importFortschritt.duplikate > 0 && <span style={{ color: 'var(--ld-warning-text)' }}>{importFortschritt.duplikate} übersprungen</span>}
              </span>
            </div>
            <div style={{ height: 6, background: '#E5E5EA', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ height: '100%', background: '#4ECDC4', borderRadius: 3, width: `${(importFortschritt.fertig / importFortschritt.gesamt) * 100}%`, transition: 'width .3s' }} />
            </div>
          </div>
        )}

        <button
          onClick={onAlleUebernehmen}
          disabled={anzahl === 0 || (importFortschritt !== null && importFortschritt.fertig < importFortschritt.gesamt)}
          style={{
            width: '100%', padding: '13px 0', borderRadius: 'var(--ld-radius-md)', border: 'none',
            background: anzahl > 0 ? 'var(--ld-primary)' : '#E5E5EA',
            color: anzahl > 0 ? 'white' : 'var(--ld-text-tertiary)',
            fontSize: 15, fontWeight: 700, cursor: anzahl > 0 ? 'pointer' : 'default',
          }}
        >
          {importFortschritt
            ? `${importFortschritt.fertig} / ${importFortschritt.gesamt} importiert …`
            : `${anzahl} Schüler/in${anzahl !== 1 ? 'nen' : ''} importieren`
          }
        </button>
      </div>
    );
  }

  return (
    <LdSheet open={true} onClose={onClose} maxWidth={500}
      kopf={{ titel: 'Klasse einrichten', kontext: 'Wie möchten Sie Ihre Schülerinnen und Schüler hinzufügen?' }}>
        {/* Welle Z3: Kopf im K4-Rahmen */}
        <div>

          {/* Tab-Leiste */}
          <div style={{ display: 'flex', gap: 4, background: 'var(--ld-bg-app)', borderRadius: 'var(--ld-radius-md)', padding: 4 }}>
            {([
              { key: 'upload' as const,  label: 'Foto / Datei' },
              { key: 'manuell' as const, label: 'Manuell' },
            ]).map(({ key, label }) => (
              <button key={key} onClick={() => setTab(key)} style={{
                flex: 1, padding: '8px 0', borderRadius: 9, border: 'none',
                background: tab === key ? 'white' : 'transparent',
                color: tab === key ? '#1C1C1E' : '#8E8E93',
                fontSize: 13, fontWeight: tab === key ? 700 : 500,
                cursor: 'pointer',
                boxShadow: tab === key ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
                transition: 'all .15s',
              }}>{label}</button>
            ))}
          </div>
        </div>

        {/* Tab-Inhalt */}
        <div style={{ padding: '20px 24px 24px' }}>

          {/* ── Klassen-Bestätigungs-UI ── */}
          {tab === 'upload' && klassenPruefung && (
            <div>
              <p style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 700, color: 'var(--ld-text-primary)' }}>
                Neue Klassen erkannt
              </p>
              <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--ld-text-secondary)', lineHeight: 1.5 }}>
                Folgende Klassen wurden im Upload erkannt, sind aber noch nicht angelegt.
                Wählen Sie für jede Klasse eine Aktion:
              </p>
              {klassenPruefung.klassen.map((k, i) => (
                <div key={i} style={{ border: '1.5px solid var(--ld-outline-soft)', borderRadius: 'var(--ld-radius-md)', padding: '12px 14px', marginBottom: 10 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--ld-text-primary)', marginBottom: 8 }}>
                    Klasse {k.name}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {(['klassenleiter', 'fachlehrer', 'skip'] as const).map(opt => (
                      <button key={opt} onClick={() => setKlassenPruefung(prev => prev && ({
                        ...prev,
                        klassen: prev.klassen.map((x, j) => j === i ? { ...x, rolle: opt } : x),
                      }))} style={{
                        padding: '6px 12px', borderRadius: 'var(--ld-radius-sm)', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        border: k.rolle === opt ? '2px solid var(--ld-primary)' : '1.5px solid var(--ld-outline-soft)',
                        background: k.rolle === opt ? 'var(--ld-primary-light, #EDE9F8)' : 'var(--ld-bg-input)',
                        color: k.rolle === opt ? 'var(--ld-primary)' : 'var(--ld-text-secondary)',
                      }}>
                        {opt === 'klassenleiter' ? 'Klassenleitung' : opt === 'fachlehrer' ? 'Fachlehrer' : 'Überspringen'}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button onClick={() => {
                  setKlassenPruefung(null);
                  klassenConfirmResolveRef.current?.(false);
                  klassenConfirmResolveRef.current = null;
                }} style={{ flex: 1, padding: '11px 0', borderRadius: 'var(--ld-radius-md)', border: '1.5px solid var(--ld-outline-soft)', background: 'var(--ld-bg-input)', fontSize: 14, fontWeight: 600, cursor: 'pointer', color: 'var(--ld-text-secondary)' }}>
                  Abbrechen
                </button>
                <button onClick={klassenBestaetigen} style={{ flex: 2, padding: '11px 0', borderRadius: 'var(--ld-radius-md)', border: 'none', background: 'var(--ld-primary)', color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                  Bestätigen &amp; importieren
                </button>
              </div>
            </div>
          )}

          {/* ── UPLOAD (Foto + Datei kombiniert) ── */}
          {tab === 'upload' && !klassenPruefung && (
            <div>
              <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--ld-text-secondary)', lineHeight: 1.5 }}>
                Foto oder Datei (PDF, CSV) hochladen — die KI extrahiert alle Namen automatisch.
              </p>
              <input ref={fotoRef} type="file" accept="image/*" onChange={onFotoPick} style={{ display: 'none' }} />
              <input ref={dateiRef} type="file" accept=".pdf,.csv,.txt,image/*" onChange={onDateiPick} style={{ display: 'none' }} />

              {/* Noch nichts ausgewählt → zwei Buttons */}
              {!fotoPreview && !dateiFile && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <button onClick={() => fotoRef.current?.click()} style={{
                    padding: '24px 0', border: '2px dashed #D1D5DB', borderRadius: 'var(--ld-radius-lg)',
                    background: 'var(--ld-bg-input)', cursor: 'pointer', fontSize: 13, color: '#6B7280',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                  }}>
                    <MIcon name="photo_camera" size={30} />Foto aufnehmen
                  </button>
                  <button onClick={() => dateiRef.current?.click()} style={{
                    padding: '24px 0', border: '2px dashed #D1D5DB', borderRadius: 'var(--ld-radius-lg)',
                    background: 'var(--ld-bg-input)', cursor: 'pointer', fontSize: 13, color: '#6B7280',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                  }}>
                    <MIcon name="upload_file" size={30} />Datei hochladen
                  </button>
                </div>
              )}

              {/* Foto-Vorschau */}
              {fotoPreview && (
                <div>
                  <div style={{ position: 'relative', marginBottom: 12 }}>
                    <img src={fotoPreview} alt="Vorschau" style={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 'var(--ld-radius-md)', display: 'block' }} />
                    <button onClick={() => { setFotoPreview(''); setFotoNamen([]); setFotoSel([]); if (fotoRef.current) fotoRef.current.value = ''; }} style={{ position: 'absolute', top: 8, right: 8, width: 28, height: 28, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.55)', color: 'white', cursor: 'pointer', fontSize: 14 }}>×</button>
                  </div>
                  {fotoNamen.length === 0 && (
                    <button onClick={analysiereFoto} disabled={fotoLaden} style={{ width: '100%', padding: '13px 0', borderRadius: 'var(--ld-radius-md)', border: 'none', background: fotoLaden ? 'var(--ld-primary-hover)' : 'var(--ld-primary)', color: 'white', fontSize: 15, fontWeight: 700, cursor: fotoLaden ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
                      {fotoLaden && <Spinner />}{fotoLaden ? 'KI analysiert …' : 'Namen extrahieren'}
                    </button>
                  )}
                  {fotoFehler && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 'var(--ld-radius-md)', padding: '10px 14px', fontSize: 13, color: '#DC2626', marginBottom: 10 }}>{fotoFehler}</div>}
                  {fotoNamen.length > 0 && (
                    <NamenListe namen={fotoNamen} sel={fotoSel}
                      duplikatSet={new Set(fotoNamen.map((n, i) => ({ n, i })).filter(({ n }) => schuelerListe.some(s => s.name.toLowerCase() === `${n.vorname} ${n.nachname}`.toLowerCase())).map(({ i }) => i))}
                      onToggle={i => setFotoSel(s => s.map((v, j) => j === i ? !v : v))}
                      onAlleUebernehmen={uebernehmenFoto} />
                  )}
                </div>
              )}

              {/* Datei-Vorschau */}
              {dateiFile && !fotoPreview && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: '#F0F9FF', borderRadius: 'var(--ld-radius-md)', marginBottom: 12 }}>
                    <MIcon name="description" size={24} />
                    <div style={{ flex: 1, fontSize: 14, color: 'var(--ld-text-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dateiName}</div>
                    <IconButton variant="bare" size={28} label="Datei entfernen" onClick={() => { setDateiFile(null); setDateiName(''); setDateiNamen([]); setDateiSel([]); if (dateiRef.current) dateiRef.current.value = ''; }} style={{ fontSize: 18 }}>×</IconButton>
                  </div>
                  {dateiNamen.length === 0 && (
                    <button onClick={analysiereDatei} disabled={dateiLaden} style={{ width: '100%', padding: '13px 0', borderRadius: 'var(--ld-radius-md)', border: 'none', background: dateiLaden ? 'var(--ld-primary-hover)' : 'var(--ld-primary)', color: 'white', fontSize: 15, fontWeight: 700, cursor: dateiLaden ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
                      {dateiLaden && <Spinner />}{dateiLaden ? 'KI analysiert …' : 'Namen extrahieren'}
                    </button>
                  )}
                  {dateiFehler && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 'var(--ld-radius-md)', padding: '10px 14px', fontSize: 13, color: '#DC2626', marginBottom: 10 }}>{dateiFehler}</div>}
                  {dateiNamen.length > 0 && (
                    <NamenListe namen={dateiNamen} sel={dateiSel}
                      duplikatSet={new Set(dateiNamen.map((n, i) => ({ n, i })).filter(({ n }) => schuelerListe.some(s => s.name.toLowerCase() === `${n.vorname} ${n.nachname}`.toLowerCase())).map(({ i }) => i))}
                      onToggle={i => setDateiSel(s => s.map((v, j) => j === i ? !v : v))}
                      onAlleUebernehmen={uebernehmenDatei} />
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── MANUELL (ld-Form-Anstrich) ── */}
          {tab === 'manuell' && (
            <div>
              {/* Icon-Chip-Kopf */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <span style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0, background: 'var(--ld-subtab-active-bg)', color: 'var(--ld-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <MIcon name="person_add" size={20} />
                </span>
                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--ld-text-primary)' }}>Schüler/in anlegen</span>
              </div>

              {hinzgefuegt > 0 && (
                <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 'var(--ld-radius-md)', padding: '10px 14px', fontSize: 13, color: 'var(--ld-success)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <MIcon name="check_circle" size={15} /> {hinzgefuegt} Schüler/in{hinzgefuegt !== 1 ? 'nen' : ''} hinzugefügt
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                <div>
                  <label style={labelSt}><MIcon name="person" size={12} />Vorname</label>
                  <input value={vorname} onChange={e => setVorname(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSpeichern()}
                    placeholder="Emma" style={inputSt} />
                </div>
                <div>
                  <label style={labelSt}><MIcon name="badge" size={12} />Nachname</label>
                  <input value={nachname} onChange={e => setNachname(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSpeichern()}
                    placeholder="Müller" style={inputSt} />
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={labelSt}><MIcon name="school" size={12} />Klasse</label>
                {/* Schnellwahl-Chips → setzen denselben setKlasse-State (kein neuer State, kein Eingriff in den Submit-Pfad) */}
                {klassenChips.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                    {klassenChips.map(c => {
                      const aktiv = klasse === c;
                      return (
                        <button key={c} type="button" onClick={() => setKlasse(c)} style={{
                          padding: '6px 13px', borderRadius: 'var(--ld-radius-pill)', border: 'none', cursor: 'pointer',
                          fontSize: 13, fontWeight: 600,
                          background: aktiv ? 'var(--ld-brand-gradient)' : 'var(--ld-bg-input)',
                          color: aktiv ? '#fff' : 'var(--ld-text-secondary)',
                          boxShadow: aktiv ? 'var(--ld-shadow-accent)' : 'none',
                        }}>{c}</button>
                      );
                    })}
                  </div>
                )}
                <input value={klasse} onChange={e => setKlasse(e.target.value)}
                  placeholder="z.B. 10b" style={inputSt} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                <div>
                  <label style={labelSt}><MIcon name="alternate_email" size={12} />E-Mail Schüler/in</label>
                  <input value={mEmail} onChange={e => setMEmail(e.target.value)}
                    placeholder="schueler@schule.de" style={inputSt} type="email" />
                </div>
                <div>
                  <label style={labelSt}><MIcon name="calendar_today" size={12} />Geburtsdatum</label>
                  <input value={mGeburtsdatum} onChange={e => setMGeburtsdatum(e.target.value)}
                    placeholder="TT.MM.JJJJ" style={inputSt} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                <div>
                  <label style={labelSt}><MIcon name="mail" size={12} />E-Mail Eltern <span style={{ color: 'var(--ld-primary)' }}>★</span></label>
                  <input value={mElternEmail} onChange={e => setMElternEmail(e.target.value)}
                    placeholder="eltern@beispiel.de" style={inputSt} type="email" />
                </div>
                <div>
                  <label style={labelSt}><MIcon name="call" size={12} />Telefon Eltern</label>
                  <input value={mElternTel} onChange={e => setMElternTel(e.target.value)}
                    placeholder="+49 171 …" style={inputSt} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={handleSpeichern} disabled={!vorname.trim() || !nachname.trim()} style={{
                  flex: 1, padding: '12px 0', borderRadius: 'var(--ld-radius-md)', border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  background: (!vorname.trim() || !nachname.trim()) ? 'var(--ld-bg-skeleton)' : 'var(--ld-brand-gradient)',
                  color: (!vorname.trim() || !nachname.trim()) ? 'var(--ld-text-tertiary)' : 'white',
                  fontSize: 15, fontWeight: 700, cursor: (!vorname.trim() || !nachname.trim()) ? 'default' : 'pointer',
                  boxShadow: (!vorname.trim() || !nachname.trim()) ? 'none' : 'var(--ld-shadow-accent)',
                }}>
                  <MIcon name="check" size={18} /> Hinzufügen
                </button>
                <button onClick={onClose} style={{
                  padding: '12px 18px', borderRadius: 'var(--ld-radius-md)', border: '1px solid var(--ld-field-border)',
                  background: 'var(--ld-bg-card)', color: 'var(--ld-text-secondary)', fontSize: 15, fontWeight: 600, cursor: 'pointer',
                }}>
                  {hinzgefuegt > 0 ? 'Fertig' : 'Abbrechen'}
                </button>
              </div>
            </div>
          )}
        </div>
    </LdSheet>
  );
}
